import Images from "../utils/Images";
import { hashHref } from "./site";

export const ABOUT_NAV = [
  { id: "who-we-are", label: "Who we are" },
  { id: "our-charter", label: "Charter" },
  { id: "what-we-do", label: "What we do" },
  { id: "ecosystem", label: "Ecosystem" },
  { id: "journey", label: "Journey" },
  { id: "impact", label: "Impact" },
  { id: "leadership", label: "Leadership" },
  { id: "partners", label: "Partners" },
];

export const FOUNDATIONS = [
  {
    index: "01",
    title: "Knowledge",
    copy: "Clear education so every financial decision starts with understanding.",
  },
  {
    index: "02",
    title: "Trust",
    copy: "Verified institutions, transparent products, and secure digital experiences.",
  },
  {
    index: "03",
    title: "Confidence",
    copy: "Intelligence that helps people and institutions act with certainty.",
  },
];

export const VALUES = [
  {
    title: "Innovation",
    copy: "We embrace technology to solve real financial challenges.",
    icon: "bulb",
  },
  {
    title: "Trust",
    copy: "We build secure, transparent and reliable digital experiences.",
    icon: "shield",
  },
  {
    title: "Customer First",
    copy: "Every solution begins with understanding customer needs.",
    icon: "users",
  },
  {
    title: "Integrity",
    copy: "We uphold the highest standards of ethics and accountability.",
    icon: "scale",
  },
  {
    title: "Collaboration",
    copy: "We grow through strong partnerships and shared success.",
    icon: "handshake",
  },
  {
    title: "Excellence",
    copy: "We strive for quality, continuous improvement and measurable impact.",
    icon: "award",
  },
];

export const PURPOSE_WORDS = ["Simple", "Transparent", "Accessible", "Intelligent", "Secure"];

export const CONSUMER_ENABLE = [
  "Learn about financial products",
  "Compare multiple providers",
  "Receive AI-powered recommendations",
  "Connect with verified institutions",
  "Access financial services confidently",
];

export const INSTITUTION_ENABLE = [
  "Acquire qualified customers",
  "Verify customer and asset information",
  "Reduce fraud",
  "Make smarter lending decisions",
  "Recover outstanding debts more efficiently",
  "Improve customer engagement throughout the customer lifecycle",
];

export const PRODUCTS = [
  {
    id: "simmarkets",
    index: "01",
    name: "SimMarkets",
    role: "Intelligent Marketplace & Customer Acquisition",
    tagline: "Discover, compare and understand before you decide.",
    copy: "SimMarkets helps consumers discover, compare and understand financial products before making decisions. Using financial education, AI-assisted recommendations and intelligent product comparison, SimMarkets connects consumers with verified financial institutions while delivering highly qualified leads that improve conversion rates.",
    capabilities: [
      "Financial Education",
      "AI Recommendation Engine",
      "Product Comparison",
      "Appointment Booking",
      "Qualified Lead Generation",
      "Customer Engagement",
      "Contact Centre as a Service",
      "Rewards & Loyalty",
    ],
    logo: Images.simmarket_colored,
    art: Images.solutionMarket,
    href: "/simmarkets",
    tone: "light",
  },
  {
    id: "simverify",
    index: "02",
    name: "SimVerify",
    role: "AI-Powered Digital Trust Intelligence",
    tagline: "Trust is the foundation of every financial transaction.",
    copy: "SimVerify enables financial institutions to verify borrowers, vehicles, devices and behavioural data before approving loans or completing high-value transactions. Its AI-powered Trust Intelligence Engine combines multiple data sources to help institutions reduce fraud, improve lending quality and make faster, smarter decisions.",
    capabilities: [
      "Borrower Verification",
      "Vehicle Verification",
      "Device Verification",
      "SIM Behaviour Intelligence",
      "AI Trust Scoring",
      "Fraud Detection",
      "Asset Verification",
      "API Integration Services",
    ],
    logo: Images.simverify_logo,
    art: Images.solutionVerify,
    href: "/simverify",
    tone: "dark",
  },
  {
    id: "simcollect",
    index: "03",
    name: "SimCollect",
    role: "Intelligent Debt Recovery & Collections",
    tagline: "Recovery that protects the relationship.",
    copy: "SimCollect transforms debt recovery into an intelligent digital experience. Instead of relying on manual follow-ups, SimCollect automates customer engagement, payment reminders, collections and recovery workflows through multiple communication channels. The platform enables financial institutions to improve recovery rates while maintaining positive customer relationships.",
    capabilities: [
      "Debt Portfolio Management",
      "Customer Engagement",
      "Automated Collections",
      "Contact Centre Operations",
      "Payment Management",
      "Mobile Money Integration",
      "USSD Payments",
      "Recovery Analytics",
      "Executive Dashboards",
    ],
    logo: Images.simcollect_logo,
    art: Images.solutionCollect,
    href: "/simcollect",
    tone: "light",
  },
];

export const JOURNEY_PHASES = [
  { id: "market", label: "SimMarkets", range: "01–03", span: "col-span-3" },
  { id: "verify", label: "SimVerify", range: "04–05", span: "col-span-2" },
  { id: "collect", label: "SimCollect", range: "06", span: "col-span-1" },
  { id: "together", label: "Together", range: "07", span: "col-span-1" },
];

export const JOURNEY_STEPS = [
  {
    n: "01",
    product: "SimMarkets",
    phase: "market",
    title: "Discover",
    copy: "Consumers discover financial information through SimMarkets.",
    href: hashHref("simmarket"),
    icon: "search",
  },
  {
    n: "02",
    product: "SimMarkets",
    phase: "market",
    title: "Learn & compare",
    copy: "They learn, compare products and receive AI-powered recommendations.",
    href: hashHref("simmarket"),
    icon: "book",
  },
  {
    n: "03",
    product: "SimMarkets",
    phase: "market",
    title: "Connect",
    copy: "Qualified customer leads are securely shared with financial institutions.",
    href: hashHref("simmarket"),
    icon: "handshake",
  },
  {
    n: "04",
    product: "SimVerify",
    phase: "verify",
    title: "Verify",
    copy: "SimVerify establishes digital trust by validating borrowers and assets.",
    href: "/simverify",
    icon: "shield",
  },
  {
    n: "05",
    product: "SimVerify",
    phase: "verify",
    title: "Decide",
    copy: "Financial institutions make faster, more informed lending decisions.",
    href: "/simverify",
    icon: "check",
  },
  {
    n: "06",
    product: "SimCollect",
    phase: "collect",
    title: "Engage & recover",
    copy: "SimCollect supports ongoing customer engagement, repayments and responsible debt recovery.",
    href: "/simcollect",
    icon: "repeat",
  },
  {
    n: "07",
    product: "Ecosystem",
    phase: "together",
    title: "Grow together",
    copy: "Consumers and institutions benefit from a trusted, seamless and connected financial experience.",
    href: hashHref("contact"),
    icon: "trend",
  },
];

export const IMPACT = [
  {
    id: "consumers",
    index: "01",
    title: "For Consumers",
    lede: "We simplify access to financial services through:",
    items: [
      "Financial education",
      "Transparent product comparison",
      "AI-assisted recommendations",
      "Trusted providers",
      "Faster access to financial products",
      "Better financial decisions",
    ],
    image: Images.aboutConsumer,
    imageLabel: "01  ·  Consumers",
    alt: "A young African professional reviewing financial options on a phone with clarity and confidence",
    spec: "Portrait 4:5 · 1200×1500 · West African consumer using a phone in warm natural light. No UI chrome or text in frame. File: /images/about/consumer.png",
  },
  {
    id: "institutions",
    index: "02",
    title: "For Financial Institutions",
    lede: "We help organizations:",
    items: [
      "Generate qualified customer leads",
      "Improve customer acquisition",
      "Reduce fraud",
      "Improve credit quality",
      "Automate collections",
      "Increase operational efficiency",
      "Enhance customer experience",
      "Grow sustainably",
    ],
    image: Images.aboutInstitution,
    imageLabel: "02  ·  Institutions",
    alt: "Banking professionals collaborating around a verification and lending dashboard",
    spec: "Portrait 4:5 · 1200×1500 · African bankers or credit officers in a modern office. File: /images/about/institution.png",
  },
  {
    id: "economy",
    index: "03",
    title: "For Africa's Economy",
    lede: "Our ecosystem contributes to:",
    items: [
      "Financial Inclusion",
      "Responsible Lending",
      "Digital Trust",
      "Innovation",
      "Economic Growth",
      "Stronger Financial Institutions",
      "Digital Transformation",
    ],
    image: Images.aboutFintech,
    imageLabel: "03  ·  Economy",
    alt: "A connected African city and digital infrastructure suggesting inclusive economic growth",
    spec: "Portrait 4:5 · 1200×1500 · Editorial scene of African digital infrastructure or urban commerce. File: /images/about/fintech.png",
  },
];

export const PARTNER_BENEFITS = [
  "Access to qualified, intent-driven customers",
  "AI-powered customer matching",
  "Reduced customer acquisition costs",
  "Better conversion rates",
  "Real-time analytics and reporting",
  "Secure API integrations",
  "Trusted digital verification services",
  "Intelligent debt recovery capabilities",
  "Scalable technology infrastructure",
  "Dedicated partnership support",
];

export const PARTNER_TYPES = [
  "Banks",
  "Insurance companies",
  "Fintechs",
  "Savings and loans",
  "Pension providers",
  "Investment companies",
];

export const EXPERTISE = [
  "Financial Services",
  "Artificial Intelligence",
  "Digital Transformation",
  "Banking Technology",
  "Cybersecurity",
  "Software Engineering",
  "Customer Experience",
  "Product Innovation",
  "Risk Management",
  "Business Strategy",
];

export const LEADERSHIP = [
  {
    name: "Full name",
    role: "Position title",
    image: Images.aboutTeam1,
    linkedin: "",
    spec: "Headshot 3:4 · 900×1200 · Professional portrait on a quiet navy or warm studio backdrop. File: /images/about-page/team-01.png",
  },
  {
    name: "Full name",
    role: "Position title",
    image: Images.aboutTeam2,
    linkedin: "",
    spec: "Headshot 3:4 · 900×1200 · Professional portrait, consistent lighting with card 01. File: /images/about-page/team-02.png",
  },
  {
    name: "Full name",
    role: "Position title",
    image: Images.aboutTeam3,
    linkedin: "",
    spec: "Headshot 3:4 · 900×1200 · Professional portrait, consistent lighting with card 01. File: /images/about-page/team-03.png",
  },
  {
    name: "Full name",
    role: "Position title",
    image: Images.aboutTeam4,
    linkedin: "",
    spec: "Headshot 3:4 · 900×1200 · Professional portrait, consistent lighting with card 01. File: /images/about-page/team-04.png",
  },
];

export const PARTNERS = [
  { id: "ecobank", name: "Ecobank", group: "financial", logo: Images.partnerEcobank },
  { id: "gcb", name: "GCB Bank", group: "financial", logo: Images.partnerGcb },
  { id: "stanbic", name: "Stanbic Bank", group: "financial", logo: Images.partnerStanbic },
  { id: "zenith", name: "Zenith Bank", group: "financial", logo: Images.partnerZenith },
  { id: "enterprise", name: "Enterprise Group", group: "insurance", logo: Images.partnerEnterprise },
  { id: "glico", name: "GLICO Group", group: "insurance", logo: Images.partnerGlico },
  { id: "hollard", name: "Hollard", group: "insurance", logo: Images.partnerHollard },
  { id: "sic", name: "SIC Insurance", group: "insurance", logo: Images.partnerSic },
];

export const PARTNER_GROUPS = [
  { id: "all", label: "All partners" },
  { id: "financial", label: "Financial Institutions" },
  { id: "insurance", label: "Insurance Sector" },
];

export const HERO_MEDIA = {
  src: Images.aboutPageHero,
  alt: "Editorial illustration of SimFinance connecting consumers, institutions and technology across Africa",
  label: "Hero illustration",
  spec: "Landscape 4:5 or 1:1 · 1600×2000 · Editorial photograph or illustration of West African professionals collaborating around a connected digital platform. Navy, gold and royal lighting. No logos or text in the image. File: /images/about-page/hero.png",
};

export const WHO_MEDIA = {
  src: Images.aboutPageWho,
  fallback: Images.aboutLandscape,
  alt: "SimFinance team bridging consumers and financial institutions through technology",
  label: "Who we are",
  spec: "Landscape 16:10 · 1920×1200 · Candid of a multidisciplinary African product and partnership team in a modern workspace. File: /images/about-page/who.png",
};

export const CEO_MEDIA = {
  src: Images.aboutPageCeo,
  alt: "Portrait of the Chief Executive Officer of SimFinance",
  label: "CEO portrait",
  spec: "Portrait 3:4 · 1200×1600 · Formal head-and-shoulders portrait, navy wardrobe, soft studio light, looking toward camera. File: /images/about-page/ceo.png",
};
